// [2026-10-21] ADDED — OpenObserve browser RUM + logs instrumentation.
//
// ADDITIVE ONLY: this file does not touch any existing business logic, styling, routing,
// state, or API call. It is imported once from the app entry (src/main.jsx) and called
// before the app mounts.
//
// SAFETY CONTRACT: initRum() can never throw, never blocks render, and no-ops entirely
// when the client token is absent (e.g. local dev, or a build where the org secret isn't
// wired up yet). If the SDK itself fails to initialize for any reason, the error is
// swallowed so the dashboard keeps working.
import { openobserveRum } from '@openobserve/browser-rum'
import { openobserveLogs } from '@openobserve/browser-logs'

// This repo's name, used as both the RUM applicationId and the service tag so events from
// this app are identifiable in OpenObserve alongside the other InCruiter front ends.
const APPLICATION_ID = 'proctoring-client-frontend'
const SERVICE_NAME = 'proctoring-client-frontend'

// The API host this dashboard talks to (see .env.example — VITE_SDK_API). Used to scope
// distributed tracing headers to requests this app actually makes, same-origin requests,
// and any other *.incruiter.com host.
const API_BASE_URL = import.meta.env.VITE_SDK_API || ''

function isTracedUrl(url) {
  try {
    const target = new URL(url, window.location.origin)
    if (target.origin === window.location.origin) return true
    if (/(^|\.)incruiter\.com$/i.test(target.hostname)) return true
    if (API_BASE_URL) {
      const apiOrigin = new URL(API_BASE_URL).origin
      if (target.origin === apiOrigin) return true
    }
    return false
  } catch {
    return false
  }
}

export function initRum() {
  if (typeof window === 'undefined') return

  // OPENOBSERVE_CLIENT_TOKEN is a public, write-only RUM token injected at build time.
  const clientToken = import.meta.env.VITE_OPENOBSERVE_CLIENT_TOKEN

  if (!clientToken) return

  try {
    const sharedConfig = {
      applicationId: APPLICATION_ID,
      service: SERVICE_NAME,
      clientToken,
      site: 'monitor.incruiter.com',
      organizationIdentifier: 'default',
      env: 'development',
      version: '0.0.1',
      insecureHTTP: false,
      apiVersion: 'v1',
    }

    openobserveRum.init({
      ...sharedConfig,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      defaultPrivacyLevel: 'mask',
      allowedTracingUrls: [
        {
          match: isTracedUrl,
          propagatorTypes: ['tracecontext', 'openobserve'],
        },
      ],
    })

    openobserveRum.startSessionReplayRecording()

    openobserveLogs.init({
      ...sharedConfig,
      forwardErrorsToLogs: true,
    })
  } catch (err) {
    console.warn('[rum] OpenObserve RUM failed to initialize; continuing without it.', err)
  }
}
